import type { Context } from '@model/context';
import type { AudioService } from '@service/audio';
import type { Command } from './command';
import { Scope } from '@type/event';

/**
 * Routes pointer/touch movement into guidance sonification.
 *
 * Constructed directly by `Mousebindingservice` rather than through
 * `CommandFactory` because pointer events carry coordinates the
 * keyboard-oriented factory contract does not accept.
 *
 * Keyboard commands rely on hotkeys-js scope routing — only the active
 * scope receives the event, so commands don't self-validate. DOM
 * pointer listeners receive every event regardless of scope, so this
 * command must filter via {@link isInTraceScope}. The check is unique
 * to this code path; there is no peer implementation to keep in sync.
 */
export class PointerGuidanceCommand implements Command {
  private readonly context: Context;
  private readonly audioService: AudioService;

  constructor(context: Context, audioService: AudioService) {
    this.context = context;
    this.audioService = audioService;
  }

  /**
   * No-ops outside `Scope.TRACE` so guidance stays silent while modals or
   * other scopes own input.
   */
  public execute(event?: Event): void {
    // Pointer-leave / missing-coordinate / out-of-scope events all need to
    // clear in-flight guidance: otherwise a throttle slot armed during a
    // prior trace-scope hover would silently delay the next beep when the
    // user returns to trace scope.
    if (!event || !this.hasClientCoordinates(event) || !this.isInTraceScope()) {
      this.reset();
      return;
    }

    const { clientX, clientY } = event;
    const guidance = this.context.moveToPointAndGetPointerGuidance(clientX, clientY);
    this.audioService.playPointerGuidance(guidance);
  }

  /** Clears in-flight guidance and the throttle so a fresh entry starts clean. */
  public reset(): void {
    this.audioService.playPointerGuidance(null);
  }

  /**
   * Click-mode entry point: snaps to the nearest point without a guidance
   * beep, leaving the regular Observer-chain sonification to fire. The
   * runtime coordinate check stays despite the narrowed parameter type
   * because exotic event subclasses can still arrive at the listener.
   */
  public executeNavigateOnly(event: PointerEvent | MouseEvent): void {
    if (!this.isInTraceScope() || !this.hasClientCoordinates(event)) {
      return;
    }
    this.context.moveToPointAndGetPointerGuidance(event.clientX, event.clientY);
  }

  private isInTraceScope(): boolean {
    return this.context.scope === Scope.TRACE;
  }

  private hasClientCoordinates(
    event: Event,
  ): event is Event & { clientX: number; clientY: number } {
    const candidate = event as Partial<{ clientX: unknown; clientY: unknown }>;
    return typeof candidate.clientX === 'number' && typeof candidate.clientY === 'number';
  }
}
